import React, { useEffect } from 'react';

const FlyweightPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Flyweight Pattern - Structural Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Flyweight Pattern - minimize memory usage by sharing objects efficiently. Learn with text editor and game particle examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Flyweight Pattern - minimize memory usage by sharing objects efficiently. Learn with text editor and game particle examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'flyweight pattern, design patterns, structural patterns, memory optimization, object sharing, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'flyweight pattern, design patterns, structural patterns, memory optimization, object sharing, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Flyweight Pattern</h1>
        <p style={styles.subtitle}>
          Structural Design Pattern - Minimize memory usage by sharing objects
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Flyweight pattern minimizes memory usage by sharing efficiently among similar objects. 
            It separates intrinsic state (shared among objects) from extrinsic state (unique to each 
            object), allowing many objects to share the same flyweight instance.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            When your application needs to create a large number of similar objects, memory consumption 
            can become a problem. The Flyweight pattern reduces memory usage by sharing common parts 
            of object state among multiple objects instead of storing all data in each object.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Text Editor Character Rendering</h2>
          <div style={styles.codeBlock}>
{`// Flyweight interface
interface CharacterFlyweight {
    void render(int x, int y, String color, int fontSize);
}

// Concrete flyweight - stores intrinsic state (shared)
class Character implements CharacterFlyweight {
    private final char symbol;        // Intrinsic state - shared
    private final String fontFamily;  // Intrinsic state - shared
    private final boolean isBold;     // Intrinsic state - shared
    private final boolean isItalic;  // Intrinsic state - shared
    
    public Character(char symbol, String fontFamily, boolean isBold, boolean isItalic) {
        this.symbol = symbol;
        this.fontFamily = fontFamily;
        this.isBold = isBold;
        this.isItalic = isItalic;
    }
    
    @Override
    public void render(int x, int y, String color, int fontSize) {
        // Extrinsic state (x, y, color, fontSize) is passed as parameters
        System.out.printf("Rendering '%c' at (%d,%d) in %s, size %d, font: %s%s%s%n",
            symbol, x, y, color, fontSize, fontFamily,
            isBold ? " Bold" : "",
            isItalic ? " Italic" : "");
    }
    
    // Getters for intrinsic state
    public char getSymbol() { return symbol; }
    public String getFontFamily() { return fontFamily; }
    public boolean isBold() { return isBold; }
    public boolean isItalic() { return isItalic; }
    
    @Override
    public String toString() {
        return String.format("Character{%c, %s%s%s}", 
            symbol, fontFamily,
            isBold ? " Bold" : "",
            isItalic ? " Italic" : "");
    }
}

// Flyweight factory - manages flyweight instances
class CharacterFactory {
    private static final Map<String, CharacterFlyweight> flyweights = new HashMap<>();
    private static int createdFlyweights = 0;
    
    public static CharacterFlyweight getCharacter(char symbol, String fontFamily, 
                                                 boolean isBold, boolean isItalic) {
        // Create key for the flyweight
        String key = symbol + "-" + fontFamily + "-" + isBold + "-" + isItalic;
        
        CharacterFlyweight flyweight = flyweights.get(key);
        if (flyweight == null) {
            flyweight = new Character(symbol, fontFamily, isBold, isItalic);
            flyweights.put(key, flyweight);
            createdFlyweights++;
            System.out.println("Created new flyweight: " + flyweight);
        }
        
        return flyweight;
    }
    
    public static int getCreatedFlyweightsCount() {
        return createdFlyweights;
    }
    
    public static int getTotalFlyweights() {
        return flyweights.size();
    }
    
    public static void printStatistics() {
        System.out.println("=== Flyweight Factory Statistics ===");
        System.out.println("Total unique flyweights created: " + createdFlyweights);
        System.out.println("Current flyweights in pool: " + flyweights.size());
        System.out.println("Memory saved by sharing flyweights!");
    }
}

// Context - stores extrinsic state
class CharacterContext {
    private int x, y;           // Extrinsic state - position
    private String color;       // Extrinsic state - color
    private int fontSize;       // Extrinsic state - size
    private CharacterFlyweight flyweight; // Reference to shared flyweight
    
    public CharacterContext(int x, int y, String color, int fontSize, 
                           char symbol, String fontFamily, boolean isBold, boolean isItalic) {
        this.x = x;
        this.y = y;
        this.color = color;
        this.fontSize = fontSize;
        // Get flyweight from factory (shared instance)
        this.flyweight = CharacterFactory.getCharacter(symbol, fontFamily, isBold, isItalic);
    }
    
    public void render() {
        flyweight.render(x, y, color, fontSize);
    }
    
    // Getters and setters for extrinsic state
    public void setPosition(int x, int y) {
        this.x = x;
        this.y = y;
    }
    
    public void setColor(String color) {
        this.color = color;
    }
    
    public void setFontSize(int fontSize) {
        this.fontSize = fontSize;
    }
    
    public int getX() { return x; }
    public int getY() { return y; }
    public String getColor() { return color; }
    public int getFontSize() { return fontSize; }
}

// Text document using flyweights
class TextDocument {
    private List<CharacterContext> characters = new ArrayList<>();
    private String documentName;
    
    public TextDocument(String documentName) {
        this.documentName = documentName;
    }
    
    public void addCharacter(int x, int y, String color, int fontSize, 
                           char symbol, String fontFamily, boolean isBold, boolean isItalic) {
        CharacterContext context = new CharacterContext(x, y, color, fontSize, 
                                                       symbol, fontFamily, isBold, isItalic);
        characters.add(context);
    }
    
    public void addText(String text, int startX, int startY, String color, int fontSize,
                       String fontFamily, boolean isBold, boolean isItalic) {
        int x = startX;
        int y = startY;
        int charWidth = fontSize / 2; // Approximate character width
        
        for (char c : text.toCharArray()) {
            if (c == ' ') {
                x += charWidth; // Space character
            } else if (c == '\\n') {
                x = startX;     // New line
                y += fontSize + 2;
            } else {
                addCharacter(x, y, color, fontSize, c, fontFamily, isBold, isItalic);
                x += charWidth;
            }
        }
    }
    
    public void render() {
        System.out.println("\\n=== Rendering Document: " + documentName + " ===");
        for (CharacterContext character : characters) {
            character.render();
        }
    }
    
    public void changeAllColorsTo(String newColor) {
        System.out.println("\\nChanging all character colors to: " + newColor);
        for (CharacterContext character : characters) {
            character.setColor(newColor);
        }
    }
    
    public void changeFontSizeBy(int delta) {
        System.out.println("\\nChanging all font sizes by: " + delta);
        for (CharacterContext character : characters) {
            character.setFontSize(character.getFontSize() + delta);
        }
    }
    
    public int getCharacterCount() {
        return characters.size();
    }
    
    public void printStatistics() {
        System.out.println("\\n=== Document Statistics ===");
        System.out.println("Document: " + documentName);
        System.out.println("Total characters: " + characters.size());
        System.out.println("Unique flyweights: " + CharacterFactory.getTotalFlyweights());
        System.out.println("Memory efficiency: " + 
            String.format("%.1f%%", (1.0 - (double)CharacterFactory.getTotalFlyweights() / characters.size()) * 100));
    }
}

// Usage
public class FlyweightDemo {
    public static void main(String[] args) {
        System.out.println("=== Flyweight Pattern Demo ===\\n");
        
        // Create a text document
        TextDocument document = new TextDocument("Sample Document");
        
        // Add some text with different formatting
        document.addText("Hello World!", 10, 10, "Black", 12, "Arial", false, false);
        document.addText("This is BOLD text", 10, 30, "Red", 14, "Arial", true, false);
        document.addText("This is italic text", 10, 50, "Blue", 12, "Times", false, true);
        document.addText("Mixed formatting!", 10, 70, "Green", 16, "Arial", true, true);
        
        // Add repeated characters (demonstrates flyweight sharing)
        document.addText("aaaaaaaaaa", 10, 90, "Purple", 12, "Arial", false, false);
        document.addText("bbbbbbbbbb", 10, 110, "Orange", 12, "Arial", false, false);
        
        // Render the document
        document.render();
        
        // Show statistics
        document.printStatistics();
        CharacterFactory.printStatistics();
        
        // Demonstrate extrinsic state changes
        System.out.println("\\n=== Demonstrating Extrinsic State Changes ===");
        document.changeAllColorsTo("DarkGray");
        document.changeFontSizeBy(2);
        
        // Render again to show changes
        System.out.println("\\nAfter changes:");
        document.render();
        
        // Create another document to show flyweight reuse
        System.out.println("\\n=== Creating Second Document ===");
        TextDocument document2 = new TextDocument("Second Document");
        document2.addText("Hello Again!", 5, 5, "Cyan", 12, "Arial", false, false);
        document2.addText("Reusing flyweights", 5, 25, "Magenta", 14, "Times", true, false);
        
        document2.render();
        document2.printStatistics();
        
        // Final statistics showing flyweight reuse
        System.out.println("\\n=== Final Statistics ===");
        CharacterFactory.printStatistics();
        System.out.println("Total characters across all documents: " + 
            (document.getCharacterCount() + document2.getCharacterCount()));
        System.out.println("Flyweight instances reused between documents!");
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Memory Efficiency:</strong> Dramatically reduces memory usage for large numbers of objects</li>
            <li style={styles.listItem}><strong>Performance:</strong> Fewer objects mean less garbage collection overhead</li>
            <li style={styles.listItem}><strong>Sharing:</strong> Common state is shared among multiple objects</li>
            <li style={styles.listItem}><strong>Scalability:</strong> Enables applications to handle millions of objects</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When you need to create a large number of similar objects</li>
            <li style={styles.listItem}>When memory usage is a critical concern</li>
            <li style={styles.listItem}>When objects contain both intrinsic and extrinsic state</li>
            <li style={styles.listItem}>When groups of objects can share common state</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Flyweight pattern is essential for memory-intensive applications. It's commonly 
            used in text editors, games (for particles, bullets), graphics systems, and any 
            application that needs to create large numbers of similar objects efficiently.
          </p>
        </section>
      </main>
    </article>
  );
};

export default FlyweightPattern;
